<script lang="ts">
  import { onDestroy, onMount } from 'svelte';
  import { quintOut } from 'svelte/easing';
  import type { Unsubscriber } from 'svelte/store';
  import { fade, slide } from 'svelte/transition';
  import LocationDetailsSide from '$lib/components/LocationDetailsSide.svelte';
  import { drawerOpen, expandedLocation, windowWidth } from '$lib/stores';
  import { t } from '$lib/translations';

  let unsubscribe: Unsubscriber | undefined;
  onMount(() => {
    unsubscribe = windowWidth.subscribe((newWindowWidth) => {
      $drawerOpen = newWindowWidth > 1024;
    });

    window.addEventListener('resize', () => {
      windowWidth.set(window.innerWidth);
    });
  });

  onDestroy(() => {
    window.removeEventListener('resize', () => {
      windowWidth.set(window.innerWidth);
    });

    if (unsubscribe) unsubscribe();
  });

  expandedLocation.subscribe((val) => {
    if (!val) {
      const el = document.getElementById('location-details');
      if (el) {
        el.style.display = 'inline-block';
      }
    }
  });

  const handleDrawerCloseClick = () => {
    $drawerOpen = !$drawerOpen;
  };
</script>

{#if $expandedLocation}
  <LocationDetailsSide location={$expandedLocation} />
{/if}

{#if $drawerOpen}
  <div
    transition:slide={{
      delay: 150,
      duration: 300,
      easing: quintOut,
      axis: 'x',
    }}
    class={`drawer ${$drawerOpen ? 'block' : 'hidden'}`}
  >
    {#if $expandedLocation}
      <div class="greyout"></div>
    {/if}
    <button
      type="button"
      class="drawer-close-btn"
      on:click={handleDrawerCloseClick}
    >
      <svg
        aria-hidden="true"
        class="w-5 h-5"
        fill="currentColor"
        viewBox="0 0 20 20"
        xmlns="http://www.w3.org/2000/svg"
        ><path
          fill-rule="evenodd"
          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
          clip-rule="evenodd"
        /></svg
      >
      <span class="sr-only">{ $t('content.closeMenu') }</span>
    </button>
    <slot />
  </div>
{:else}
  <button
    transition:fade={{
      delay: 250,
      duration: 300,
    }}
    class="toggle-button"
    on:click={handleDrawerCloseClick}
  >
    { $t('content.findStores') }
  </button>
{/if}

<style lang="postcss">
  :global(.drawer) {
    @apply bg-white text-gray-800 fixed right-0 z-40 h-screen overflow-y-auto transition-transform md:w-[390px] lg:w-[420px] xl:w-[450px] shadow-xl;
    border-left: 1px solid #e9ebf0;
    top: 1rem;
    height: calc(100vh - 2rem);
    border-top-left-radius: 1.5rem;
    border-bottom-left-radius: 1.5rem;
  }

  :global(.leftMenu .drawer) {
    @apply left-0;
    right: unset;
    border-top-right-radius: 1.5rem;
    border-bottom-right-radius: 1.5rem;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  :global(.greyout) {
    @apply bg-gray-900 fixed right-0 z-50 h-screen overflow-y-auto transition-transform md:w-[390px] lg:w-[420px] xl:w-[450px] opacity-25;
    top: 1rem;
    height: calc(100vh - 2rem);
    border-top-left-radius: 1.5rem;
    border-bottom-left-radius: 1.5rem;
  }

  :global(.leftMenu .greyout) {
    @apply left-0;
    right: unset;
    border-top-right-radius: 1.5rem;
    border-bottom-right-radius: 1.5rem;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  .drawer-close-btn {
    @apply lg:hidden text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 absolute top-2.5 right-2.5 inline-flex items-center;
  }

  .toggle-button {
    @apply fixed bottom-0 right-0 text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 mr-2 mb-2;
  }
</style>
